import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Select, { MenuProps, components } from 'react-select';
import { useField, useFormikContext } from 'formik';
//import { handleKeyPress } from '@rabbit/components/shared-ui';
import { InputTypeAutoCompleteConsumerShape } from '@rabbit/elements/shared-types';
import { useDebounce } from '../../../utils/hooks/useDebounce';
import { generateSelectStyles } from '../../../utils/consts';
import { CORPORATE } from '@rabbit/sage/utils/consts';
import Bus from '@rabbit/bus';
import { InstantSearch, Configure, useHits } from 'react-instantsearch';
import { FBD_HoldingProxy } from '@rabbit/data/types';
import { GetSearchIndexName } from '../../../../../../../search/cherchons/src/lib/config';
import { GetAlgoliaSearchClient } from '../../../../../../../search/cherchons/src/lib/client';
import { useGetConsumerList } from '@rabbit/bizproc/react';
import { AppContext } from '@rabbit/app-context';
import { useTranslation } from 'react-i18next';

// Define a type for Algolia hits used by this component
interface ConsumerHit {
  objectID: string;
  payload?: {
    docid?: string;
    consumer?: string;
    consumer_email?: string;
    consumer_name?: string;
    business_name?: string;
    fullname?: string;
    email?: string;
    mfr?: string | string[];
  };
}

// Stable HitsBridge component to avoid remount loops
function HitsBridge({
  onOptions,
  onLoaded,
  type,
}: {
  onOptions: (opts: OptionShape[]) => void;
  onLoaded: () => void;
  type: string;
}) {
  const { hits } = useHits<ConsumerHit>();
  const prevSigRef = useRef<string>('');

  useEffect(() => {
    const ids =
      hits?.map((hit) => hit.objectID ?? hit.payload?.docid ?? '') || [];
    const sig = JSON.stringify(ids);
    if (sig === prevSigRef.current) return; // no meaningful change
    prevSigRef.current = sig;

    const uniqueMap = new Map<string, OptionShape>();
    for (const hit of hits || []) {
      const payload = hit.payload ?? (hit as unknown as any);
      const id = payload?.consumer || payload?.docid || hit.objectID || '';
      const emailRaw = payload?.consumer_email || payload?.email || '';
      const name =
        payload?.consumer_name ||
        payload?.fullname ||
        payload?.business_name ||
        '';
      if (!emailRaw && !name) continue;
      const key = (emailRaw || '').toLowerCase() || `id:${id}`;
      if (!uniqueMap.has(key)) {
        const option = createConsumerOption(hit as any, type);
        if (option) uniqueMap.set(key, option);
      }
    }
    const newOptions = Array.from(uniqueMap.values());
    onOptions(newOptions);
    onLoaded();
  }, [hits, type, onOptions, onLoaded]);

  return null;
}

export interface InputAutoCompleteConsumerProps {
  name: string;
  settings: InputTypeAutoCompleteConsumerShape;
  onChange?: (...args: any) => any; // todo: review
}

interface OptionShape {
  id: string;
  label: string;
  item: object;
  value: string;
  nameHtml?: string;
  emailHtml?: string;
}

function normalizeHighlightedValue(value?: string): string | undefined {
  if (!value) return value;
  let s = value
    .replace(/__ais-highlight__/g, '<em>')
    .replace(/__\/ais-highlight__/g, '</em>');
  s = s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return s;
}

// Shared utility to map Firestore items or Algolia hits into an OptionShape
function createConsumerOption(item: any, type: any): OptionShape | null {
  const payload = item?.payload ?? item;
  const id = payload?.consumer || payload?.docid || item?.objectID || '';
  const emailRaw = payload?.consumer_email || payload?.email || '';
  const name =
    payload?.consumer_name || payload?.fullname || payload?.business_name || '';

  if (!emailRaw && !name) return null;

  const hl = item?._highlightResult?.payload ?? item?._highlightResult;
  const nameHtml = normalizeHighlightedValue(
    hl?.consumer_name?.value || hl?.fullname?.value || hl?.business_name?.value
  );
  const emailHtml = normalizeHighlightedValue(
    hl?.consumer_email?.value || hl?.email?.value
  );

  return {
    id,
    label:
      (type === CORPORATE ? payload?.business_name || name : name) +
      (emailRaw ? ` (${emailRaw})` : ''),
    value: emailRaw || id,
    item: payload,
    nameHtml,
    emailHtml,
  } as OptionShape;
}

export function InputAutoCompleteConsumer({
  name,
  settings,
  onChange: defaultOnChange,
}: InputAutoCompleteConsumerProps) {
  const { setFieldValue } = useFormikContext();
  const {
    placeholder,
    isMulti = false,
    isClearable = true,
    disabled = false,
    customButton,
    token,
    type,
    facetFilter,
    ...props
  } = settings;
  const { config } = useContext(AppContext);
  const ALGOLIA_SEARCH_ENABLED =
    config?.CUSTOMER?.ALGOLIA_SEARCH_ENABLED === true;
  const { t } = useTranslation();
  const mfrValue = `M:${t('tenantLink')}`;

  // Firestore-backed consumer list for normal search when Algolia is disabled

  const [field, meta] = useField(name);

  // Hooks must run unconditionally in the component body. This used to be
  // called inside the effect below, which React rejects with "Invalid hook
  // call" and blanked the New Registration modal.
  const { getConsumersByEmail } = useGetConsumerList(
    token ?? null,
    type,
    config?.CUSTOMER?.CORPORATE_ACCOUNT === true
  );

  const [options, setOptions] = useState<OptionShape[]>([]);
  const [inputText, setInputText] = useState('');
  // Note: Debouncing is actually not currently required here as the consumerList query is just being filtered.
  // Setting it to 100ms just to maintain the rest of the code as is, since this doesn't have a major performance impact and
  // it may be handled through an api in the near future. - DC

  const debouncedInput = useDebounce(inputText, 100);
  const [isLoadingResults, setIsLoadingResults] = useState(false);

  // Algolia client and index for consumers
  const searchClient = useMemo(
    () => GetAlgoliaSearchClient(FBD_HoldingProxy),
    []
  );
  const indexName = useMemo(() => GetSearchIndexName(FBD_HoldingProxy), []);

  let errorField = false;
  if (meta?.error) errorField = true;

  function onChange(option: any) {
    if (isMulti) {
      void setFieldValue(
        name,
        option ? option.map((item: { value: any }) => item.value) : []
      );
    } else {
      void setFieldValue(name, option?.value ? option.value : '');
    }
    defaultOnChange && defaultOnChange(option);
  }

  const getValue = () => {
    if (options) {
      return isMulti
        ? options.filter(
            (option: { value: any }) => field.value.indexOf(option.value) >= 0
          )
        : options.find(
            (option: { value: any }) => option.value === field.value
          );
    } else {
      return isMulti ? [] : '';
    }
  };

  useEffect(() => {
    if (debouncedInput.length) {
      if (ALGOLIA_SEARCH_ENABLED) {
        setIsLoadingResults(true);
        // Options will be set by HitsBridge via react-instantsearch
      } else {
        // Firestore-backed normal search
        setIsLoadingResults(true);
        const data = getConsumersByEmail(debouncedInput) || [];
        const newOptions: OptionShape[] = [];
        for (const item of data as any[]) {
          const option = createConsumerOption(item, type);
          if (option) newOptions.push(option);
        }
        setOptions(newOptions);
        setIsLoadingResults(false);
      }
    } else if (settings?.options && settings?.options?.length > 0) {
      setOptions(settings?.options as OptionShape[]);
      setIsLoadingResults(false);
    } else {
      setOptions([]);
      setIsLoadingResults(false);
    }
  }, [
    debouncedInput,
    settings?.options,
    type,
    ALGOLIA_SEARCH_ENABLED,
    getConsumersByEmail,
  ]);

  const onInputChange = (e: string) => {
    setInputText(e);
  };

  // Stable callback to mark loading as finished after hits processed
  const onLoaded = useCallback(() => {
    setIsLoadingResults(false);
  }, []);

  const CustomMenu = (props: MenuProps<any>) => {
    return (
      <Fragment>
        <components.Menu {...props}>
          <div className="font-nunito text-base font-semibold text-gray-900">
            {type === CORPORATE ? 'Business' : 'Customers'}
          </div>
          {props.children}
          {customButton && (
            <div className="mt-2 w-full border-t border-gray-300 pt-2">
              {customButton}
            </div>
          )}
        </components.Menu>
      </Fragment>
    );
  };

  interface ItemData {
    fullname?: string;
    email?: string;
  }

  interface OptionData {
    item?: ItemData;
  }

  const CustomOption = (props: any) => {
    const { innerProps, isDisabled, data } = props as {
      innerProps: any;
      isDisabled: boolean;
      data: OptionShape & OptionData;
    };
    const item = (data as any)?.item;
    const name =
      (item as any)?.business_name ||
      (item as any)?.consumer_name ||
      (item as any)?.fullname ||
      '-';
    const email = (item as any)?.consumer_email || (item as any)?.email || '-';
    const nameHtml = (data as any)?.nameHtml as string | undefined;
    const emailHtml = (data as any)?.emailHtml as string | undefined;
    return !isDisabled && item ? (
      <div
        {...innerProps}
        className="font-nunito focus-within:bg-primary-100 active:bg-primary-100 hover:!bg-primary-100 my-1 !cursor-pointer overflow-hidden rounded !bg-gray-50 p-2 text-gray-900"
      >
        <div className="font-nunito text-base font-semibold">
          {nameHtml ? (
            <span dangerouslySetInnerHTML={{ __html: nameHtml }} />
          ) : (
            name
          )}
        </div>
        <div className="font-nunito text-sm text-gray-500">
          {emailHtml ? (
            <span dangerouslySetInnerHTML={{ __html: emailHtml }} />
          ) : (
            email
          )}
        </div>
      </div>
    ) : null;
  };

  return (
    <div>
      {/* Only mount InstantSearch when there is input and Algolia search is enabled */}
      {debouncedInput.length > 0 && ALGOLIA_SEARCH_ENABLED && (
        <InstantSearch
          indexName={indexName}
          searchClient={searchClient}
          future={{ preserveSharedStateOnUnmount: true }}
        >
          <Configure
            query={debouncedInput}
            hitsPerPage={10}
            distinct={true}
            facetFilters={facetFilter ?? []}
            highlightPreTag="<b style='color: var(--color-green-800)' className='text-green-800'>"
            highlightPostTag="</b>"
            attributesToHighlight={[
              'payload.consumer_name',
              'payload.consumer_email',
            ]}
            attributesToRetrieve={[
              'payload.consumer',
              'payload.consumer_email',
              'payload.consumer_name',
            ]}
          />
          <HitsBridge
            onOptions={setOptions}
            onLoaded={onLoaded}
            type={type ?? ''}
          />
        </InstantSearch>
      )}

      <Select
        {...field}
        {...props}
        options={options}
        isMulti={isMulti}
        classNames={generateSelectStyles(errorField)}
        placeholder={placeholder}
        defaultValue={field.value}
        maxMenuHeight={200}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          Menu: CustomMenu,
          Option: CustomOption,
        }}
        value={options ? getValue() : ''}
        onChange={onChange}
        onMenuOpen={() => Bus.emit(Bus.events.modalContent, true)}
        onMenuClose={() => Bus.emit(Bus.events.modalContent, false)}
        onInputChange={onInputChange}
        isClearable={isClearable}
        isDisabled={disabled}
        noOptionsMessage={({ inputValue }) =>
          !inputValue
            ? 'Type something...'
            : inputText === debouncedInput && !isLoadingResults
            ? 'No results found'
            : 'Loading...'
        }
      />
    </div>
  );
}

export default InputAutoCompleteConsumer;
